import type { Episode, EvidenceLink, FindingSource } from '../api/types';
import { evidenceClassLabel, formatDate, formatRange, relationMeta } from '../lib/format';

interface Moment {
  key: string;
  date: string | null;
  dateBasis: string;
  link: EvidenceLink;
  source: FindingSource | undefined;
}

/**
 * Dates come only from established fields: a pinned version's source-created or
 * source-edited time. Observation (capture) time is never presented as when
 * something happened, so undated evidence is grouped and labelled as undated.
 */
function momentsFor(episode: Episode, sources: Map<string, FindingSource>): Moment[] {
  return episode.evidence.map((link, index) => {
    const source = sources.get(link.sourceId);
    const version = source?.versions?.find((candidate) => candidate.id === link.versionId);
    const created = version?.sourceCreatedAt ?? null;
    const edited = version?.sourceEditedAt ?? null;
    return {
      key: `${link.sourceId}|${link.versionId ?? ''}|${String(index)}`,
      date: created ?? edited,
      dateBasis: created !== null ? 'Created at source' : edited !== null ? 'Edited at source' : 'Date not established',
      link,
      source,
    };
  });
}

function sortMoments(moments: Moment[]): { dated: Moment[]; undated: Moment[] } {
  const dated = moments.filter((moment) => moment.date !== null);
  dated.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  return { dated, undated: moments.filter((moment) => moment.date === null) };
}

function MomentItem({ moment }: { moment: Moment }) {
  const classLabel = evidenceClassLabel(moment.link.metadata?.evidenceClass);
  return (
    <li className={`timeline__moment timeline__moment--${moment.link.relation}`}>
      <span className="timeline__marker" aria-hidden="true" />
      <div className="timeline__body">
        <p className="timeline__date">
          {moment.date === null ? moment.dateBasis : `${formatDate(moment.date)} · ${moment.dateBasis.toLowerCase()}`}
        </p>
        <p className="timeline__what">
          <span className={`relation relation--${moment.link.relation}`}>{relationMeta[moment.link.relation].label}</span>
          {classLabel !== null && <span className="timeline__class">{classLabel}</span>}
        </p>
        <p className="timeline__note">{moment.link.note ?? moment.source?.title ?? 'Source not visible to your account'}</p>
        {moment.source !== undefined && moment.link.note !== undefined && (
          <p className="timeline__source">{moment.source.title}</p>
        )}
      </div>
    </li>
  );
}

/** An annotated sequence of the evidence inside each episode. */
export function EpisodeTimeline({ episodes, sources }: { episodes: Episode[]; sources: FindingSource[] }) {
  const byId = new Map(sources.map((source) => [source.id, source]));
  if (episodes.length === 0) {
    return <p className="muted">No episode is linked to this finding version.</p>;
  }
  return (
    <div className="episodes">
      {episodes.map((episode) => {
        const { dated, undated } = sortMoments(momentsFor(episode, byId));
        return (
          <article key={episode.id} className="episode" aria-labelledby={`episode-${episode.id}`}>
            <header className="episode__head">
              <p className="eyebrow">Episode · {formatRange(episode.startAt, episode.endAt)}</p>
              <h3 id={`episode-${episode.id}`} className="episode__title">
                {episode.title}
              </h3>
              <p className="episode__summary">{episode.summary}</p>
            </header>
            {dated.length > 0 && (
              <ol className="timeline" aria-label="Dated evidence, earliest first">
                {dated.map((moment) => (
                  <MomentItem key={moment.key} moment={moment} />
                ))}
              </ol>
            )}
            {undated.length > 0 && (
              <div className="timeline-undated">
                <p className="timeline-undated__label">
                  {dated.length === 0 ? 'No evidence in this episode has an established date.' : 'Undated evidence'}{' '}
                  <span className="muted">Capture time is not used as event time.</span>
                </p>
                <ol className="timeline timeline--undated" aria-label="Evidence without an established date">
                  {undated.map((moment) => (
                    <MomentItem key={moment.key} moment={moment} />
                  ))}
                </ol>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
